import { DataItem } from 'scrivito'

import { classifyOrderStatus } from './classifyOrderStatus'
import { ClientOption, MyOrderRow, OrderStatusCategory } from '../../types'
import { currencyFormatter } from '../../utils/format'
import { TimerecordClient } from '@/Data/TimerecordClient/TimerecordClientDataClass'
import { ensureString } from '@/utils/ensureString'
import { ensureNumber } from '@/utils/timerecordUtils'

export { classifyOrderStatus }

export interface TimesheetStatus {
  billedDurationSum: number
  statuses: Set<string>
}

export const buildTimesheetStatusMap = (
  timeRecords: DataItem[],
): Map<string, TimesheetStatus> =>
  timeRecords.reduce((map, record) => {
    const orderId = ensureString(record.get('order'))
    const billedDuration = (record.get('billedDuration') as number) ?? 0
    const status = ensureString(record.get('status'))

    const existing = map.get(orderId)
    if (existing) {
      existing.billedDurationSum += billedDuration
      existing.statuses.add(status)
    } else {
      map.set(orderId, {
        billedDurationSum: billedDuration,
        statuses: new Set([status]),
      })
    }
    return map
  }, new Map<string, TimesheetStatus>())

interface BudgetTotals {
  revenue: number
  revenueDays: number
  totalBudget: number
  totalDays: number
  usedBudget: number
  usedDays: number
  remainingBudget: number
  remainingDays: number
}

const readBudget = (budgetItem: DataItem | undefined): BudgetTotals => ({
  revenue: ensureNumber(budgetItem?.get('revenue')) || 0,
  revenueDays: ensureNumber(budgetItem?.get('recordedDays')) || 0,
  totalBudget: ensureNumber(budgetItem?.get('totalBudget')) || 0,
  totalDays: ensureNumber(budgetItem?.get('totalDays')) || 0,
  usedBudget: ensureNumber(budgetItem?.get('usedBudget')) || 0,
  usedDays: ensureNumber(budgetItem?.get('usedDays')) || 0,
  remainingBudget: ensureNumber(budgetItem?.get('remainingBudget')) || 0,
  remainingDays: ensureNumber(budgetItem?.get('remainingDays')) || 0,
})

const emptyTotals = (): BudgetTotals => ({
  revenue: 0,
  revenueDays: 0,
  totalBudget: 0,
  totalDays: 0,
  usedBudget: 0,
  usedDays: 0,
  remainingBudget: 0,
  remainingDays: 0,
})

const addTotals = (a: BudgetTotals, b: BudgetTotals): BudgetTotals => ({
  revenue: a.revenue + b.revenue,
  revenueDays: a.revenueDays + b.revenueDays,
  totalBudget: a.totalBudget + b.totalBudget,
  totalDays: a.totalDays + b.totalDays,
  usedBudget: a.usedBudget + b.usedBudget,
  usedDays: a.usedDays + b.usedDays,
  remainingBudget: a.remainingBudget + b.remainingBudget,
  remainingDays: a.remainingDays + b.remainingDays,
})

const to2 = (n: number) => Number(n.toFixed(2))

const toRow = (
  order: DataItem,
  budget: BudgetTotals,
  statuses: Set<string> | undefined,
  orderTypesMap: Record<string, string>,
  clientNameLookup: (id: string) => string,
): { row: MyOrderRow; customer: ClientOption } => {
  const orderId = ensureString(order?.id())
  const clientId = ensureString(order?.get('client'))
  const orderType = ensureString(order?.get('type'))
  const orderTitle = ensureString(order?.get('name'))
  const customerName = clientNameLookup(clientId)

  return {
    customer: { label: customerName, value: clientId },
    row: {
      orderId,
      clientId,
      orderType: orderTypesMap[orderType] || '-',
      orderTitle,
      customerName,
      revenue: currencyFormatter.format(budget.revenue),
      revenueDays: to2(budget.revenueDays),
      totalBudget: currencyFormatter.format(budget.totalBudget),
      totalDays: to2(budget.totalDays),
      usedBudget: currencyFormatter.format(budget.usedBudget),
      usedDays: to2(budget.usedDays),
      remainingBudget: currencyFormatter.format(budget.remainingBudget),
      remainingDays: to2(budget.remainingDays),
      status: classifyOrderStatus(statuses ?? new Set()),
    },
  }
}

export interface OrdersAggregatorInput {
  orders: DataItem[]
  budgetForOrder: (orderId: string) => DataItem | undefined
  timesheetStatus: Map<string, TimesheetStatus>
  orderTypesMap: Record<string, string>
  onlyWithRevenue?: boolean
}

export const aggregateOrders = ({
  orders,
  budgetForOrder,
  timesheetStatus,
  orderTypesMap,
  onlyWithRevenue = false,
}: OrdersAggregatorInput) => {
  const clientNameLookup = (id: string) => {
    const clientRecord = id ? TimerecordClient.get(id) : null
    return clientRecord ? ensureString(clientRecord.get('name')) : ''
  }

  const customerMap = new Map<string, ClientOption>()
  const rows: MyOrderRow[] = []
  const totals = orders.reduce<BudgetTotals>((acc, order) => {
    const orderId = ensureString(order?.id())
    const budget = readBudget(budgetForOrder(orderId))
    const statuses = timesheetStatus.get(orderId)?.statuses
    const { row, customer } = toRow(
      order,
      budget,
      statuses,
      orderTypesMap,
      clientNameLookup,
    )
    if (onlyWithRevenue && budget.revenue <= 0) {
      return acc
    }

    customerMap.set(customer.value, customer)
    rows.push(row)
    return addTotals(acc, budget)
  }, emptyTotals())

  const customers = Array.from(customerMap.values()).sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
  )

  return {
    orders: rows,
    customers,
    summaryMetrics: {
      revenue: currencyFormatter.format(totals.revenue),
      revenueDays: to2(totals.revenueDays),
      totalBudget: currencyFormatter.format(totals.totalBudget),
      totalDays: to2(totals.totalDays),
      usedBudget: currencyFormatter.format(totals.usedBudget),
      usedDays: to2(totals.usedDays),
      remainingBudget: currencyFormatter.format(totals.remainingBudget),
      remainingDays: to2(totals.remainingDays),
    },
  } as {
    orders: MyOrderRow[]
    customers: ClientOption[]
    summaryMetrics: {
      revenue: string
      revenueDays: number
      totalBudget: string
      totalDays: number
      usedBudget: string
      usedDays: number
      remainingBudget: string
      remainingDays: number
    }
  }
}

export type { OrderStatusCategory }
